import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import ServiceSelection from './components/ServiceSelection';
import TimeSelection from './components/TimeSelection';
import RecommendedShops from './components/RecommendedShops';
import BookingGuide from './components/BookingGuide';
import SelectedShopInfo from './components/SelectedShopInfo';

export default function Booking() {
  const [searchParams] = useSearchParams();
  const shopId = searchParams.get('shopId');
  
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');

  // Shop data
  const allShops = [
    {
      id: 1,
      name: '다움네일살롱',
      location: '강남구 청담동',
      address: '서울 강남구 청담동 123-45',
      services: ['nail'],
      rating: 4.9,
      reviewCount: 328,
      distance: '0.5km',
      image: 'https://public.readdy.ai/ai/img_res/252e34dcaa166cd95bdb022e0785cacf.jpg',
      specialty: '프렌치네일 디자인으로 유명한 프리미엄 샵',
      availableSlots: 3,
      availableTimes: ['10:00', '11:00', '14:00', '15:00', '16:00']
    },
    {
      id: 2,
      name: '라벤더 아이래쉬',
      location: '마포구 홍대입구',
      address: '서울 마포구 서교동 456-78',
      services: ['eyelash'],
      rating: 4.8,
      reviewCount: 256,
      distance: '1.2km',
      image: 'https://readdy.ai/api/search-image?query=Modern%20eyelash%20extension%20salon%20with%20soft%20lavender%20and%20white%20tones%2C%20comfortable%20treatment%20bed%2C%20professional%20lighting%2C%20minimalist%20beauty%20studio%2C%20elegant%20feminine%20design%2C%20premium%20lash%20service%20space%2C%20serene%20atmosphere%2C%20clean%20aesthetic&width=400&height=300&seq=booking-shop-lash-1&orientation=landscape',
      specialty: '자연스러운 속눈썹 연장 전문',
      availableSlots: 5,
      availableTimes: ['09:00', '10:00', '11:00', '13:00', '14:00', '15:00', '17:00']
    },
    {
      id: 3,
      name: '로즈 스킨케어',
      location: '강남구 압구정동',
      address: '서울 강남구 압구정로 789-12',
      services: ['skincare'],
      rating: 5.0,
      reviewCount: 412,
      distance: '0.8km',
      image: 'https://readdy.ai/api/search-image?query=Premium%20skincare%20spa%20room%20with%20rose%20gold%20accents%2C%20soft%20pink%20lighting%2C%20luxury%20facial%20treatment%20bed%2C%20elegant%20minimalist%20interior%2C%20professional%20beauty%20clinic%2C%20warm%20beige%20tones%2C%20sophisticated%20wellness%20space%2C%20serene%20healing%20atmosphere&width=400&height=300&seq=booking-shop-skin-1&orientation=landscape',
      specialty: '맞춤형 피부관리와 힐링 케어',
      availableSlots: 2,
      availableTimes: ['10:00', '14:00', '16:00']
    }
  ];

  const selectedShop = shopId ? allShops.find(shop => shop.id === parseInt(shopId)) : null;

  useEffect(() => {
    if (selectedShop) {
      // Auto-select services that the shop provides
      setSelectedServices(selectedShop.services);
    }
  }, [selectedShop]);

  const handleServiceToggle = (service: string) => {
    if (selectedShop) {
      // Only allow services that the shop provides
      if (selectedShop.services.includes(service)) {
        setSelectedServices(prev => 
          prev.includes(service) 
            ? prev.filter(s => s !== service)
            : [...prev, service]
        );
      }
    } else {
      setSelectedServices(prev => 
        prev.includes(service) 
          ? prev.filter(s => s !== service)
          : [...prev, service]
      );
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
  };

  const showRecommendations = selectedServices.length > 0 && selectedTime && selectedDate && !selectedShop;

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/30 to-white">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-light text-gray-800 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {selectedShop ? '예약하기' : '간편 예약하기'}
            </h1>
            <p className="text-base text-gray-600">
              {selectedShop ? '원하는 서비스와 시간을 선택해주세요' : '원하는 서비스와 시간을 선택하면 맞춤 매장을 추천해드려요'}
            </p>
          </div>

          {/* Selected Shop Info */}
          {selectedShop && (
            <SelectedShopInfo shop={selectedShop} />
          )}

          {/* Progress Steps */}
          {!selectedShop && (
            <div className="flex items-center justify-center mb-12">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full ${selectedServices.length > 0 ? 'bg-gradient-to-r from-rose-400 to-pink-400' : 'bg-gray-200'} transition-all`}>
                    <span className="text-white text-sm font-medium">1</span>
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-700">서비스 선택</span>
                </div>
                <div className="w-16 h-0.5 bg-gray-200"></div>
                <div className="flex items-center">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full ${selectedTime && selectedDate ? 'bg-gradient-to-r from-rose-400 to-pink-400' : 'bg-gray-200'} transition-all`}>
                    <span className="text-white text-sm font-medium">2</span>
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-700">시간 선택</span>
                </div>
                <div className="w-16 h-0.5 bg-gray-200"></div>
                <div className="flex items-center">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full ${showRecommendations ? 'bg-gradient-to-r from-rose-400 to-pink-400' : 'bg-gray-200'} transition-all`}>
                    <span className="text-white text-sm font-medium">3</span>
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-700">매장 선택</span>
                </div>
              </div>
            </div>
          )}

          {/* Service Selection */}
          <ServiceSelection 
            selectedServices={selectedServices}
            onServiceToggle={handleServiceToggle}
            availableServices={selectedShop?.services}
          />

          {/* Time Selection */}
          <TimeSelection 
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            onDateSelect={handleDateSelect}
            onTimeSelect={handleTimeSelect}
            availableTimes={selectedShop?.availableTimes}
          />

          {/* Recommended Shops */}
          {showRecommendations && (
            <RecommendedShops 
              selectedServices={selectedServices}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
            />
          )}

          {/* Booking Guide */}
          <BookingGuide />
        </div>
      </main>

      <Footer />
    </div>
  );
}